import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, Loader2, CalendarClock, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

export default function ManageSubscriptionPanel() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["my-subscription"], queryFn: () => api.get("/memberships/my-subscription").then((r) => r.data) });

  if (isLoading || !data?.active) return null;

  const cancel = async () => {
    if (!window.confirm("Cancel your membership? You'll keep access until the end of your current billing period.")) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/memberships/subscription/cancel");
      toast.success(r.access_until ? `Cancelled. You keep access until ${fmtDate(r.access_until)}.` : "Membership cancelled.");
      qc.invalidateQueries({ queryKey: ["my-subscription"] });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      await api.post("/memberships/subscription/reactivate");
      toast.success("Renewal turned back on — welcome back!");
      qc.invalidateQueries({ queryKey: ["my-subscription"] });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const period = data.interval === "year" ? "year" : "month";

  return (
    <div className="eyw-card p-8 mt-8" data-testid="manage-subscription-panel">
      <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><BadgeCheck size={20} className="text-[#5C8A72]" /> Your membership</h2>
      <div className="mt-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#E5E0D8] bg-[#F9F7F3] p-5">
        <div>
          <div className="text-lg font-semibold text-[#1A242B]" data-testid="sub-plan-name">{data.plan}</div>
          <div className="text-sm text-[#4A5D6B]">{money(Number(data.amount))}/{period}
            {data.since ? ` · member since ${fmtDate((data.since || "").slice(0, 10))}` : ""}</div>
          {data.next_billing && (
            data.cancel_at_period_end ? (
              <div className="text-sm text-[#B5794E] mt-1.5 flex items-center gap-1.5" data-testid="sub-cancel-notice">
                <CalendarClock size={15} /> Access until {fmtDate(data.next_billing)} — won't renew.
              </div>
            ) : (
              <div className="text-sm text-[#4A5D6B] mt-1.5 flex items-center gap-1.5" data-testid="sub-next-billing">
                <CalendarClock size={15} /> Renews {fmtDate(data.next_billing)}
              </div>
            )
          )}
        </div>
        {!data.cancel_at_period_end && (
          <button onClick={cancel} disabled={busy} data-testid="sub-cancel-btn"
            className="eyw-btn-ghost !py-2 text-sm !text-red-600 hover:!bg-red-50 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <><XCircle size={15} /> Cancel membership</>}
          </button>
        )}
        {data.cancel_at_period_end && (
          <button onClick={reactivate} disabled={busy} data-testid="sub-reactivate-btn"
            className="eyw-btn !py-2 text-sm disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <><RefreshCw size={15} /> Reactivate membership</>}
          </button>
        )}
      </div>
    </div>
  );
}
